import { createElement, useState } from "react";
import { Trans } from "react-i18next";
import { useDotLegalSnackbar } from "@dotlegal/dotlegal-ui-components";
import { deleteHttp } from "@common/api/apiShared";
import { useDotLegalMutation, useDotLegalQuery } from "@common/hooks/dotLegalReactQuery";
import { useTranslation } from "@localization/useTranslation";
import { useUserContext } from "../../../auth/userContextProvider/UserContextProvider";
import { useMasterDataBreadCrumbProvider } from "../../useMasterDataBreadCrumbProvider";
import { IncidentType } from "./IncidentTypes.types";

export const incidentTypesUrl = "/incidentTypes";

export default function useIncidentTypesHooks() {
    const { isLoading, data: incidentTypes, refetch } = useDotLegalQuery<Array<IncidentType>>(incidentTypesUrl);
    const [showCreateDialog, setShowCreateDialog] = useState(false);
    const [incidentTypeForDeletion, setIncidentTypeForDeletion] = useState<IncidentType | undefined>(undefined);
    const [incidentTypeForUpdate, setIncidentTypeForUpdate] = useState<IncidentType | undefined>(undefined);
    const { translateString } = useTranslation();
    const snackBar = useDotLegalSnackbar();
    const { permissions } = useUserContext();
    const masterDataBreadCrumbProvider = useMasterDataBreadCrumbProvider();
    const breadCrumbs = masterDataBreadCrumbProvider.getBreadCrumbs(translateString("incidentTypesMasterData"));

    const deleteMutation = useDotLegalMutation(deleteIncidentType, {
        onSuccess: async (result) => {
            if (result.success()) {
                await refetch();
                snackBar.show(createElement(Trans, { i18nKey: "itemDeletedSuccess", values: { name: incidentTypeForDeletion?.name } }));
            } else {
                snackBar.show(createElement(Trans, { i18nKey: "cantDeleteIncidentType", values: { name: incidentTypeForDeletion?.name } }), "warning");
            }
            setIncidentTypeForDeletion(undefined);
        },
    });

    function deleteIncidentType(id: string) {
        return deleteHttp(`${incidentTypesUrl}/${id}`);
    }

    const onCreateClick = () => {
        setShowCreateDialog(true);
    };

    const onDialogClose = () => {
        setIncidentTypeForUpdate(undefined);
        setShowCreateDialog(false);
    };

    const onSaved = async () => {
        await refetch();
        onDialogClose();
    };

    const onDelete = () => {
        deleteMutation.mutate(incidentTypeForDeletion!.id);
    };

    const getMenuItems = (incidentType: IncidentType) => {
        const menuItems = [];
        if (permissions.canManageMasterData) {
            menuItems.push({
                menuItem: translateString("edit"),
                onClick: () => setIncidentTypeForUpdate(incidentType),
            });

            menuItems.push({
                menuItem: translateString("delete"),
                onClick: () => setIncidentTypeForDeletion(incidentType),
            });
        }

        return menuItems;
    };

    return {
        isLoading,
        incidentTypes,
        breadCrumbs,
        getMenuItems,
        canManageMasterData: permissions.canManageMasterData,
        showCreateDialog,
        onSaved,
        incidentTypeForDeletion,
        incidentTypeForUpdate,
        setIncidentTypeForDeletion,
        onDelete,
        onCreateClick,
        onDialogClose,
        isDeleting: deleteMutation.isLoading,
    };
}
